import React, { useState, useEffect, useRef } from 'react';
import { FiFilter, FiX } from 'react-icons/fi';
import { getApiEndpoint } from '../utils/api';
import { getAuthHeaders } from '../utils/auth';
import './Filters.css';

const Filters = ({ filters, onFilterChange, isLoggedIn, facets = { journals: [], institutions: [] } }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);

  // Facet search state
  const [journalSearch, setJournalSearch] = useState('');
  const [institutionSearch, setInstitutionSearch] = useState('');

  // Author autocomplete state
  const [authorQuery, setAuthorQuery] = useState('');
  const [authorSuggestions, setAuthorSuggestions] = useState([]);
  const [showAuthorDropdown, setShowAuthorDropdown] = useState(false);
  const authorRef = useRef(null);
  const authorTimeoutRef = useRef(null);

  // Topic search state
  const [topicQuery, setTopicQuery] = useState('');
  const [topicSuggestions, setTopicSuggestions] = useState([]);
  const [showTopicDropdown, setShowTopicDropdown] = useState(false);
  const [collectionTopics, setCollectionTopics] = useState([]);
  const topicRef = useRef(null);
  const topicTimeoutRef = useRef(null);

  // Keep local state in sync when parent filters change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (authorRef.current && !authorRef.current.contains(event.target)) {
        setShowAuthorDropdown(false);
      }
      if (topicRef.current && !topicRef.current.contains(event.target)) {
        setShowTopicDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggested topics from collections on mount
  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchSuggestedTopics = async () => {
      try {
        const response = await fetch(getApiEndpoint('/api/topics/suggest'), {
          headers: getAuthHeaders(),
        });
        if (response.ok) {
          const data = await response.json();
          setCollectionTopics(data.results || []);
        }
      } catch (err) {
        console.error('Error fetching suggested topics:', err);
      }
    };
    fetchSuggestedTopics();
  }, [isLoggedIn]);

  const currentYear = new Date().getFullYear();
  const hasRelevanceThreshold = localFilters.relevanceThreshold !== undefined && localFilters.relevanceThreshold !== null && localFilters.relevanceThreshold !== '';
  const selectedTopics = localFilters.topics || [];
  const selectedJournals = localFilters.journals || [];
  const selectedInstitutions = localFilters.institutions || [];
  // authors is now an array of { id, display_name }; guard against old string format
  const selectedAuthors = Array.isArray(localFilters.authors) ? localFilters.authors : [];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearFilter = (key) => {
    const newFilters = { ...localFilters };
    delete newFilters[key];
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearAll = () => {
    setLocalFilters({});
    onFilterChange({});
  };

  // Topic search with debounce
  const handleTopicInputChange = (e) => {
    const value = e.target.value;
    setTopicQuery(value);

    if (topicTimeoutRef.current) {
      clearTimeout(topicTimeoutRef.current);
    }

    if (value.length >= 2) {
      topicTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await fetch(getApiEndpoint(`/api/topics/search?q=${encodeURIComponent(value)}`));
          const data = await response.json();
          // Filter out already-selected topics
          const selectedIds = new Set(selectedTopics.map(t => t.id));
          const filtered = (data.results || []).filter(t => !selectedIds.has(t.id));
          setTopicSuggestions(filtered);
          setShowTopicDropdown(true);
        } catch (err) {
          console.error('Error searching topics:', err);
        }
      }, 300);
    } else {
      setTopicSuggestions([]);
      setShowTopicDropdown(false);
    }
  };

  const handleAddTopic = (topic) => {
    const newTopics = [...selectedTopics, { id: topic.id, display_name: topic.display_name }];
    handleFilterChange('topics', newTopics);
    setTopicQuery('');
    setTopicSuggestions([]);
    setShowTopicDropdown(false);
  };

  const handleRemoveTopic = (topicId) => {
    const newTopics = selectedTopics.filter(t => t.id !== topicId);
    if (newTopics.length === 0) {
      handleClearFilter('topics');
    } else {
      handleFilterChange('topics', newTopics);
    }
  };

  // Author autocomplete handlers
  const handleAuthorInputChange = (e) => {
    const value = e.target.value;
    setAuthorQuery(value);
    if (authorTimeoutRef.current) clearTimeout(authorTimeoutRef.current);
    if (value.length >= 2) {
      authorTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await fetch(getApiEndpoint(`/api/autocomplete?q=${encodeURIComponent(value)}&type=authors`));
          const data = await response.json();
          const selectedIds = new Set(selectedAuthors.map(a => a.id));
          const filtered = (data.results || []).filter(r => !selectedIds.has(r.id.replace('https://openalex.org/', '')));
          setAuthorSuggestions(filtered);
          setShowAuthorDropdown(true);
        } catch (err) {
          console.error('Error fetching author suggestions:', err);
        }
      }, 300);
    } else {
      setAuthorSuggestions([]);
      setShowAuthorDropdown(false);
    }
  };

  const handleAddAuthor = (result) => {
    const shortId = result.id.replace('https://openalex.org/', '');
    const newAuthors = [...selectedAuthors, { id: shortId, display_name: result.title }];
    handleFilterChange('authors', newAuthors);
    setAuthorQuery('');
    setAuthorSuggestions([]);
    setShowAuthorDropdown(false);
  };

  const handleRemoveAuthor = (authorId) => {
    const newAuthors = selectedAuthors.filter(a => a.id !== authorId);
    if (newAuthors.length === 0) {
      handleClearFilter('authors');
    } else {
      handleFilterChange('authors', newAuthors);
    }
  };

  // Journal facet handlers
  const handleJournalToggle = (journal) => {
    const isSelected = selectedJournals.some(j => j.id === journal.id);
    if (isSelected) {
      const updated = selectedJournals.filter(j => j.id !== journal.id);
      updated.length === 0 ? handleClearFilter('journals') : handleFilterChange('journals', updated);
    } else {
      handleFilterChange('journals', [...selectedJournals, { id: journal.id, name: journal.name, excluded: false }]);
    }
  };

  const handleJournalExcludeToggle = (journalId) => {
    handleFilterChange('journals', selectedJournals.map(j => j.id === journalId ? { ...j, excluded: !j.excluded } : j));
  };

  const handleJournalRemove = (journalId) => {
    const updated = selectedJournals.filter(j => j.id !== journalId);
    updated.length === 0 ? handleClearFilter('journals') : handleFilterChange('journals', updated);
  };

  // Institution facet handlers
  const handleInstitutionToggle = (institution) => {
    const isSelected = selectedInstitutions.some(i => i.id === institution.id);
    if (isSelected) {
      const updated = selectedInstitutions.filter(i => i.id !== institution.id);
      updated.length === 0 ? handleClearFilter('institutions') : handleFilterChange('institutions', updated);
    } else {
      handleFilterChange('institutions', [...selectedInstitutions, { id: institution.id, name: institution.name, excluded: false }]);
    }
  };

  const handleInstitutionExcludeToggle = (institutionId) => {
    handleFilterChange('institutions', selectedInstitutions.map(i => i.id === institutionId ? { ...i, excluded: !i.excluded } : i));
  };

  const handleInstitutionRemove = (institutionId) => {
    const updated = selectedInstitutions.filter(i => i.id !== institutionId);
    updated.length === 0 ? handleClearFilter('institutions') : handleFilterChange('institutions', updated);
  };

  const activeFilterCount = Object.keys(localFilters).filter(key => {
    const val = localFilters[key];
    if (Array.isArray(val)) return val.length > 0;
    return val !== '' && val !== null;
  }).length;

  // Filter out already-selected topics from collection suggestions
  const selectedIds = new Set(selectedTopics.map(t => t.id));
  const visibleCollectionTopics = collectionTopics.filter(t => !selectedIds.has(t.id));

  return (
    <div className="filters-container">
      <div className="filters-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="filters-title">
          <FiFilter className="filter-icon" />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="filter-badge">{activeFilterCount}</span>
          )}
        </div>
        <button
          className="toggle-button"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? '−' : '+'}
        </button>
      </div>

      {isExpanded && (
        <div className="filters-content">

          {/* Journal Facets */}
          {facets.journals.length > 0 && (
            <div className="filter-group facet-section">
              <label>Journals</label>
              <input
                type="text"
                className="facet-search"
                placeholder="Filter list..."
                value={journalSearch}
                onChange={e => setJournalSearch(e.target.value)}
              />
              <div className="facet-list">
                {facets.journals
                  .filter(j => !journalSearch || j.name.toLowerCase().includes(journalSearch.toLowerCase()))
                  .map(journal => {
                    const isSelected = selectedJournals.some(j => j.id === journal.id);
                    return (
                      <label key={journal.id} className="facet-item">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleJournalToggle(journal)}
                        />
                        <span className="facet-item-name">{journal.name}</span>
                        <span className="facet-count">{journal.count.toLocaleString()}</span>
                      </label>
                    );
                  })}
              </div>
              {selectedJournals.length > 0 && (
                <div className="selected-topics">
                  {selectedJournals.map(j => (
                    <span key={j.id} className={`active-filter-tag${j.excluded ? ' facet-tag-excluded' : ''}`}>
                      {j.name}
                      <button
                        onClick={() => handleJournalExcludeToggle(j.id)}
                        title={j.excluded ? 'Click to include' : 'Click to exclude'}
                        className="facet-tag-toggle"
                      >
                        {j.excluded ? 'incl' : 'excl'}
                      </button>
                      <button onClick={() => handleJournalRemove(j.id)}><FiX /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Institution Facets */}
          {facets.institutions.length > 0 && (
            <div className="filter-group facet-section">
              <label>Institutions</label>
              <input
                type="text"
                className="facet-search"
                placeholder="Filter list..."
                value={institutionSearch}
                onChange={e => setInstitutionSearch(e.target.value)}
              />
              <div className="facet-list">
                {facets.institutions
                  .filter(i => !institutionSearch || i.name.toLowerCase().includes(institutionSearch.toLowerCase()))
                  .map(institution => {
                    const isSelected = selectedInstitutions.some(i => i.id === institution.id);
                    return (
                      <label key={institution.id} className="facet-item">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleInstitutionToggle(institution)}
                        />
                        <span className="facet-item-name">{institution.name}</span>
                        <span className="facet-count">{institution.count.toLocaleString()}</span>
                      </label>
                    );
                  })}
              </div>
              {selectedInstitutions.length > 0 && (
                <div className="selected-topics">
                  {selectedInstitutions.map(i => (
                    <span key={i.id} className={`active-filter-tag${i.excluded ? ' facet-tag-excluded' : ''}`}>
                      {i.name}
                      <button
                        onClick={() => handleInstitutionExcludeToggle(i.id)}
                        title={i.excluded ? 'Click to include' : 'Click to exclude'}
                        className="facet-tag-toggle"
                      >
                        {i.excluded ? 'incl' : 'excl'}
                      </button>
                      <button onClick={() => handleInstitutionRemove(i.id)}><FiX /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="filter-group">
            <label>Publication Year</label>
            <input
              type="number"
              min="1900"
              max={currentYear}
              placeholder="e.g., 2020"
              value={localFilters.publicationYear || ''}
              onChange={(e) => handleFilterChange('publicationYear', e.target.value)}
              className="filter-input"
            />
            {localFilters.publicationYear && (
              <button
                className="clear-filter-button"
                onClick={() => handleClearFilter('publicationYear')}
              >
                <FiX />
              </button>
            )}
          </div>

          <div className="filter-group">
            <label>Open Access</label>
            <select
              value={localFilters.openAccess || ''}
              onChange={(e) => handleFilterChange('openAccess', e.target.value)}
              className="filter-select"
            >
              <option value="">All</option>
              <option value="is_oa:true">Open Access Only</option>
              <option value="is_oa:false">Not Open Access</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Work Type</label>
            <select
              value={localFilters.type || ''}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="filter-select"
            >
              <option value="">All Types</option>
              <option value="type:article">Article</option>
              <option value="type:book">Book</option>
              <option value="type:chapter">Chapter</option>
              <option value="type:dataset">Dataset</option>
            </select>
          </div>

          <div className="filter-group topic-filter-group" ref={authorRef}>
            <label>Author Name</label>
            <input
              type="text"
              placeholder="Search by author name"
              value={authorQuery}
              onChange={handleAuthorInputChange}
              className="filter-input"
            />
            {showAuthorDropdown && authorSuggestions.length > 0 && (
              <div className="topic-dropdown">
                {authorSuggestions.map((result) => (
                  <div
                    key={result.id}
                    className="topic-dropdown-item"
                    onClick={() => handleAddAuthor(result)}
                  >
                    <span className="topic-dropdown-name">{result.title}</span>
                  </div>
                ))}
              </div>
            )}
            {selectedAuthors.length > 0 && (
              <div className="selected-topics">
                {selectedAuthors.map((author) => (
                  <span key={author.id} className="active-filter-tag">
                    {author.display_name}
                    <button onClick={() => handleRemoveAuthor(author.id)}>
                      <FiX />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="filter-group">
            <label>Min Relevance Score</label>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="e.g., 50"
              value={localFilters.relevanceThreshold ?? ''}
              onChange={(e) => handleFilterChange('relevanceThreshold', e.target.value)}
              className="filter-input"
            />
            {hasRelevanceThreshold && (
              <button
                className="clear-filter-button"
                onClick={() => handleClearFilter('relevanceThreshold')}
              >
                <FiX />
              </button>
            )}
          </div>

          {/* Research Topics - full width */}
          <div className="filter-group topic-filter-group" ref={topicRef}>
            <label>Research Topics</label>
            <input
              type="text"
              placeholder="Search topics (e.g., Transportation, Causal Inference)"
              value={topicQuery}
              onChange={handleTopicInputChange}
              className="filter-input"
            />

            {showTopicDropdown && topicSuggestions.length > 0 && (
              <div className="topic-dropdown">
                {topicSuggestions.map((topic) => (
                  <div
                    key={topic.id}
                    className="topic-dropdown-item"
                    onClick={() => handleAddTopic(topic)}
                  >
                    <span className="topic-dropdown-name">{topic.display_name}</span>
                    {topic.description && (
                      <span className="topic-hint">{topic.description}</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {selectedTopics.length > 0 && (
              <div className="selected-topics">
                {selectedTopics.map((topic) => (
                  <span key={topic.id} className="active-filter-tag">
                    {topic.display_name}
                    <button onClick={() => handleRemoveTopic(topic.id)}>
                      <FiX />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {isLoggedIn && visibleCollectionTopics.length > 0 && (
              <div className="suggested-topics">
                <span className="suggested-topics-label">From your collections:</span>
                {visibleCollectionTopics.map((topic) => (
                  <span
                    key={topic.id}
                    className="suggested-topic-chip"
                    onClick={() => handleAddTopic(topic)}
                  >
                    {topic.display_name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {activeFilterCount > 0 && (
            <button className="clear-all-button" onClick={handleClearAll}>
              Clear All Filters
            </button>
          )}
        </div>
      )}

      {activeFilterCount > 0 && !isExpanded && (
        <div className="active-filters">
          {localFilters.publicationYear && (
            <span className="active-filter-tag">
              Year: {localFilters.publicationYear}
              <button onClick={() => handleClearFilter('publicationYear')}>
                <FiX />
              </button>
            </span>
          )}
          {localFilters.openAccess && (
            <span className="active-filter-tag">
              {localFilters.openAccess === 'is_oa:true' ? 'Open Access' : 'Not Open Access'}
              <button onClick={() => handleClearFilter('openAccess')}>
                <FiX />
              </button>
            </span>
          )}
          {localFilters.type && (
            <span className="active-filter-tag">
              {localFilters.type.replace('type:', '')}
              <button onClick={() => handleClearFilter('type')}>
                <FiX />
              </button>
            </span>
          )}
          {selectedAuthors.map((author) => (
            <span key={author.id} className="active-filter-tag">
              {author.display_name}
              <button onClick={() => handleRemoveAuthor(author.id)}>
                <FiX />
              </button>
            </span>
          ))}
          {hasRelevanceThreshold && (
            <span className="active-filter-tag">
              Min relevance: {localFilters.relevanceThreshold}
              <button onClick={() => handleClearFilter('relevanceThreshold')}>
                <FiX />
              </button>
            </span>
          )}
          {selectedTopics.map((topic) => (
            <span key={topic.id} className="active-filter-tag">
              {topic.display_name}
              <button onClick={() => handleRemoveTopic(topic.id)}>
                <FiX />
              </button>
            </span>
          ))}
          {selectedJournals.map((j) => (
            <span key={j.id} className={`active-filter-tag${j.excluded ? ' facet-tag-excluded' : ''}`}>
              {j.name}
              <button onClick={() => handleJournalRemove(j.id)}><FiX /></button>
            </span>
          ))}
          {selectedInstitutions.map((i) => (
            <span key={i.id} className={`active-filter-tag${i.excluded ? ' facet-tag-excluded' : ''}`}>
              {i.name}
              <button onClick={() => handleInstitutionRemove(i.id)}><FiX /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default Filters;
